import { State } from "country-state-city";
import { isMobile, isTablet } from "react-device-detect";
import platform from "platform";

export const history = require("history").createBrowserHistory({});

export const relativeNumber = (number) => {
  if (number < 1000) return number.toString();

  const suffixes = ["", "K", "M", "B", "T"];
  const tier = Math.floor(Math.log10(number) / 3);
  const scaled = number / Math.pow(10, tier * 3);
  const roundOff = scaled.toFixed(2);

  return `${roundOff}${suffixes[tier]}`;
};

export const formatCurrency = (input) => {
  if (input === "-") return input;
  const numericString = String(input).replace(/[^0-9]/g, "");
  const number = parseInt(numericString, 10);

  if (isNaN(number) || number === 0) return `${input}`;

  if (number < 1000) return `₹  ${number.toString()}`;
  if (number < 100000) return `₹  ${(number / 1000).toFixed(1)}K`;
  if (number < 10000000) return `₹  ${(number / 100000).toFixed(1)}L`;
  return `₹  ${(number / 10000000).toFixed(1)}Cr`;
};

export const formatYear = (duration) => {
  if (duration === "Info Not Available" || duration === "Info not available*") {
    return duration;
  }

  const isAstrickPresent = duration.includes("*");
  const numericString = duration.replace(/[^0-9]/g, "");
  const formattedYear = isAstrickPresent ? `${numericString}*` : numericString;
  return `${formattedYear} Year${Number(formattedYear) > 1 ? "s" : ""}`;
};

export const getSessionColor = (session, sessionColors) => {
  const baseReferenceYear = 2022;
  const colorCycle = sessionColors.length;
  const colorIndex = (((session - baseReferenceYear) % colorCycle) + colorCycle) % colorCycle;
  return sessionColors[colorIndex];
};

export const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const num = Number(value);
  if (isNaN(num)) return "-";

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num);
};

export const calculateDateDifference = (createdAt, views) => {
  if (!createdAt) return null;
  const createdDate = new Date(createdAt);
  const currentDate = new Date();
  const diff = currentDate - createdDate;
  const differenceInMillis = Math.floor(diff / (1000 * 60 * 60 * 24));

  return relativeNumber(differenceInMillis * 10 + views);
};

let timer;
export const debounce =
  (func, timeout = 500) =>
  (...args) => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      await func(...args);
    }, [timeout]);
  };

export const downloadFile = ({ url }) => {
  if (!url) return;

  const aTag = document.createElement("a");
  aTag.href = url;
  aTag.click();
};

export const getStates = (countryCode = "IN") => {
  return State.getStatesOfCountry(countryCode);
};

export const setPhoneNumberToken = (token) => {
  localStorage.setItem("phonenumber_token", token);
};
export const getPhoneNumberToken = () => {
  return localStorage.getItem("phonenumber_token");
};

export const setUserToken = (token) => {
  localStorage.setItem("user_token", token);
};
export const getUserToken = () => {
  return localStorage.getItem("user_token");
};

export const removeUserToken = () => {
  return localStorage.removeItem("user_token");
};

export const showDefaultChoiceListSelector = (pageTitle, isGroup) => {
  switch (pageTitle) {
    case "Allotments":
    case "Closing Ranks":
      return isGroup ? false : true;
    case "Seat Matrix":
      return true;
    default:
      return false;
  }
};

export const subscribeUserNavigate = (metaData) => {
  const pathname = history.location.pathname;
  const is_subscribed = metaData?.default_exam?.is_subscribed;
  const isDashboard = pathname === "/portal" || pathname === "/portal/";
  const redirectTo =
    isDashboard && is_subscribed
      ? "/portal/dashboard"
      : pathname !== "/portal" &&
        !pathname.endsWith("faq") &&
        !pathname.endsWith("packages") &&
        !pathname.endsWith("resources") &&
        !pathname.endsWith("profile") &&
        !pathname.endsWith("package") &&
        !is_subscribed
      ? "/portal/dashboard"
      : null;
  if (redirectTo !== null) {
    history.push({
      pathname: redirectTo,
      state: {
        switch_exam: true,
      },
    });
  }
};

export const isAuthenticated = () => {
  return getUserToken();
};

export function tweakFilterParams(obj) {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (
        (Array.isArray(value) && value.length === 0) ||
        value === null ||
        value === undefined ||
        value === "" ||
        (typeof value === "object" && Object.keys(value).length === 0)
      ) {
        // Skip empty arrays, null, undefined, and empty string values
      } else {
        acc[key] = value;
      }
      return acc;
    },
    Array.isArray(obj) ? [] : {}
  );
}

function haveSameElements(oldArray, newArray) {
  if (oldArray.length !== newArray.length) {
    return false;
  }
  // Sort both arrays and compare the sorted versions
  const sortedOldArray = [...oldArray].sort();
  const sortedNewArray = [...newArray].sort();
  for (let i = 0; i < sortedOldArray.length; i++) {
    if (typeof sortedOldArray[i] === "object" && typeof sortedNewArray[i] === "object") {
      if (!hasIdenticalParams(sortedOldArray[i], sortedNewArray[i])) {
        // Recursive call for nested objects
        return false;
      }
    } else {
      if (sortedOldArray[i] !== sortedNewArray[i]) {
        return false;
      }
    }
  }
  return true;
}

export function hasIdenticalParams(savedFilters, newFilters) {
  const savedFilterKeys = Object.keys(savedFilters);
  const newFilterKeys = Object.keys(newFilters);

  if (savedFilterKeys.length !== newFilterKeys.length) {
    return false;
  }

  for (const key of savedFilterKeys) {
    const savedValue = savedFilters[key];
    const newValue = newFilters[key];

    // Check if the key is not in newFilterKeys
    if (!newFilterKeys.includes(key)) {
      return false;
    }

    // Handle different data types in a switch-case
    switch (typeof savedValue) {
      case "string":
        if (typeof newValue !== "string" || savedValue !== newValue) {
          return false;
        }
        break;
      case "number":
        if (typeof newValue !== "number" || savedValue !== newValue) {
          return false;
        }
        break;
      case "boolean":
        if (typeof newValue !== "boolean" || savedValue !== newValue) {
          return false;
        }
        break;
      case "object":
        if (Array.isArray(savedValue) && Array.isArray(newValue)) {
          if (!haveSameElements(savedValue, newValue)) {
            return false;
          }
        } else if (!Array.isArray(savedValue) && !Array.isArray(newValue)) {
          if (!hasIdenticalParams(savedValue, newValue)) {
            return false;
          }
        } else {
          return false; // mismatched types between saved and new filters
        }
        break;
      default:
        console.error("Unexpected data type:", typeof savedValue);
        return false;
    }
  }
  return true;
}

export const deviceInfo = () => {
  if (isMobile) {
    return "mobile_web";
  } else if (isTablet) {
    return "tablet_web";
  } else {
    return "web";
  }
};

export const getRelativeTime = (date) => {
  if (!date) return null;
  const createdAtTimestamp = new Date(date);
  const now = new Date();
  const elapsed = now - createdAtTimestamp;
  const timeUnits = [
    { unit: "year", divisor: 31536000000 },
    { unit: "month", divisor: 2592000000 },
    { unit: "week", divisor: 604800000 },
    { unit: "day", divisor: 86400000 },
    { unit: "hour", divisor: 3600000 },
    { unit: "minute", divisor: 60000 },
    { unit: "second", divisor: 1000 },
  ];

  for (const { unit, divisor } of timeUnits) {
    if (elapsed >= divisor) {
      let timeAgo = Math.floor(elapsed / divisor);
      return `${timeAgo} ${unit}${timeAgo > 1 ? "s" : ""} ago`;
    }
  }

  return "Just now";
};

export const nerdUpdatesDateFormat = (date) => {
  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const [weekday, month, day] = formattedDate.split(" ");
  return `${weekday} ${day} ${month}`;
};

export const dateFormat = (date) => {
  if (!date) return null;
  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return formattedDate;
};

export const timeFormat = (date) => {
  const formattedTime = new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return formattedTime;
};

export const dateTimeFormat = (date) => {
  const formattedDate = new Date(date).toLocaleString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return formattedDate;
};

export const eventInfo = (dateTime) => {
  const value = new Date(dateTime);
  return {
    month: value?.toLocaleString("default", { month: "short" }).toUpperCase(),
    day: value?.toLocaleString("default", { day: "2-digit" }),
    time: value?.toLocaleString([], {
      hour: "numeric",
      minute: "2-digit",
    }),
  };
};

export const encodedVideoId = (videoId) => {
  return btoa(videoId);
};

export const snakeToTitleCase = (str) => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export function isObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

const colors = {
  A: "#A720DD",
  B: "#40840D",
  C: "#556B2F",
  D: "#D74B1A",
  E: "#01579B",
  F: "#C10700",
  G: "#008B8B",
  H: "#BD4D0C",
  I: "#800000",
  J: "#4637B0",
  K: "#D100A2",
  L: "#5E5E5E",
  M: "#B30648",
  N: "#7CB100",
  O: "#6E00FF",
  P: "#F13939",
  Q: "#D40071",
  R: "#13019B",
  S: "#045489",
  T: "#0E85BD",
  U: "#04AC66",
  V: "#A66D3C",
  W: "#274764",
  X: "#B9A334",
  Y: "#56284F",
  Z: "#734B2E",
};

export const createUserDisplayPicture = (userName, dimension, textSize, fontWeight = "400") => {
  if (!userName) return null;
  const firstLetter = userName.charAt(0).toUpperCase();
  const color = colors[firstLetter] || "#F46C3C";

  return (
    <div
      className="rounded-full text-center flex items-center justify-center"
      style={{
        backgroundColor: `${color}`,
        width: `${dimension}px`,
        height: `${dimension}px`,
        color: "#fff",
        fontSize: `${textSize}px`,
        fontWeight: `${fontWeight}`,
      }}
    >
      {firstLetter}
    </div>
  );
};

export const hexToRgba = (hex, alpha = 1) => {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((x) => x + x)
      .join("");
  const [r, g, b] = [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const checkForSingleCounselling = (counsellingList, state) => {
  if (counsellingList) {
    const counselling = counsellingList[state];
    if (Array.isArray(counselling) && counselling.length === 1) {
      return counselling[0].id;
    }
  }
  return null;
};

export const formatDate = (isoString) => {
  const date = new Date(isoString);
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  return date.toLocaleString("en-US", options).replace(",", "");
};

export const getUtmLink = () => {
  const searchParams = window.location.search;
  return searchParams
    ? `https://play.google.com/store/apps/details?id=com.zynerd.portal&referrer=${encodeURIComponent(
        searchParams.substring(1)
      )}`
    : "https://play.google.com/store/apps/details?id=com.zynerd.portal";
};

const getAppLink = (options = {}) => {
  const isAndroid = platform.os?.toString().toLowerCase().includes("android");

  if (isAndroid) {
    if (options?.utmLink) return options?.utmLink;
    return "https://play.google.com/store/apps/details?id=com.zynerd.portal";
  }

  return "https://apps.apple.com/in/app/zynerd-neet-ug-pg-mds/id6504278658";
};

export const getDownloadAppLink = (redirectLink, options = {}) => {
  const deviceType = deviceInfo();

  if (deviceType === "web" && redirectLink) {
    return redirectLink;
  }

  return getAppLink(options);
};

export function getLanguageString(language, format = "short") {
  if (!language) return null;
  const languageMap = {
    TAMIL: "தமிழ்",
    TELUGU: "తెలుగు",
    ASSAMESE: "অসমীয়া",
    KASHMIRI: "كشميري",
    KONKANI: "कोंकणी",
    BENGALI: "বাংলা",
    DOGRI: "डोगरी",
    GUJARATI: "ગુજરાતી",
    HINDI: "हिन्दी",
    KANNADA: "ಕನ್ನಡ",
    BODO: "बर’",
    MALAYALAM: "മലയാളം",
    MANIPURI: "মেইতেইলোন",
    MARATHI: "मराठी",
    NEPALI: "नेपाली",
    ENGLISH: "English",
  };

  const original = languageMap[language];
  return format !== "short" ? `${language} - ${original}` : original;
}

export const getSource = (title) => {
  switch (title) {
    case "Allotments":
      return "ALLOTMENT";
    case "Closing Ranks":
      return "CLOSING_RANK";
    case "Seat Matrix":
      return "SEAT_MATRIX";
    case "Fee, Stipend and Bond":
      return "FEE_STIPEND_BOND";
    case "Allotment Mapping":
      return "ALLOTMENT_MAPPING";
    case "Rank Scan":
      return "RANK_SCAN";
    case "Seat Increase":
      return "SEAT_INCREASE";
    case "My Choice List":
      return "CHOICE_LIST";
    case "Announcements":
      return "ANNOUNCEMENT";
    case "Events":
      return "EVENT";
    default:
      return title;
  }
};
